import * as Yup from 'yup';
import { ILoyaltyCard } from '../../models/LoyaltyCard';
import { IUserSessionStore } from '../../models/UserSessionStore';
import { LanguageService } from '../../utils/languageService';

/**
 * StampCard form validation schema
 */
export const getStampCardValidationSchema = (t: Function, languageService: LanguageService) => {
  return Yup.object().shape({
    id: Yup.string().required(),
    restaurantIds: Yup.array(Yup.string()).required().min(1, t('stampCardModifyScreen.errors.venueRequired')),
    name: languageService.createLocalizedTextSchema(),
    reward_data: Yup.object({
      description: languageService.createLocalizedContentSchema(),
      title: languageService.createLocalizedContentSchema(),
      images: Yup.object({
        mainImage: Yup.string().nullable(),
      }),
      properties: Yup.object({
        amount: Yup.number(),
        articleId: Yup.string(),
        priceGroupId: Yup.string(),
      }),
    }),
    status: Yup.string(),
    settings: Yup.object({
      includeCap: Yup.boolean(),
      cap: Yup.number(),
      ignoreZeroAmount: Yup.boolean(),
      validItems: Yup.object({
        items: Yup.array(Yup.string()),
      })
    })
  });
};

/**
 * Default values for creating a new StampCard
 */
export const getStampCardDefaultValues = (userSessionStore: IUserSessionStore, languageService: LanguageService) => ({
  id: "NEW__",
  restaurantIds: userSessionStore?.isOrganizationContext() ? [] : [
    ...(userSessionStore?.selectedBranch?.id ? [userSessionStore?.selectedBranch?.id] : [])
  ],
  name: languageService.createLocalizedTextDefaults(),
  reward_data: {
    images: {
      mainImage: undefined,
    },
    description: languageService.createLocalizedContentDefaults(),
    title: languageService.createLocalizedContentDefaults(),
    properties: {
      amount: 1,
      articleId: "",
      priceGroupId: "1",
    }
  },
  settings: {
    includeCap: false,
    cap: 10,
    ignoreZeroAmount: false,
    validItems: {
      items: []
    }
  },
  status: 'ENABLED'
});

/**
 * Transform a StampCard model instance to form values
 */
export const transformStampCardValues = (stampCard: ILoyaltyCard) => {
  return {
    id: stampCard.id,
    restaurantIds: stampCard.restaurantIds?.map(restaurant => restaurant.id) || [],
    name: {
      text: Object.fromEntries(stampCard.name?.text || []),
    },
    reward_data: {
      description: Object.fromEntries(stampCard.reward_data?.description || []),
      title: Object.fromEntries(stampCard.reward_data?.title || []),
      properties: {
        amount: stampCard.reward_data?.properties?.amount || 1,
        articleId: stampCard.reward_data?.properties?.articleId || "",
        priceGroupId: stampCard.reward_data?.properties?.priceGroupId || "1",
      },
      images: {
        mainImage: stampCard.reward_data?.images?.mainImage || undefined,
      }
    },
    settings: {
      cap: stampCard.settings?.cap || 10,
      includeCap: stampCard.settings?.includeCap || false,
      ignoreZeroAmount: stampCard.settings?.ignoreZeroAmount || false,
      validItems: {
        items: stampCard.settings?.validItems?.items || []
      }
    },
    status: stampCard.status || 'ENABLED',
  };
};

/**
 * Prepare form values for submission to the API
 */
export const prepareStampCardForSubmission = (values: any) => ({
  id: values.id,
  name: values.name,
  status: values.status,
  settings: {
    ...values.settings,
    validItems: {
      items: values.settings.validItems.items.map((item: string) => Number(item))
    }
  },
  title: values.reward_data.title,
  description: values.reward_data.description,
  venues: values.restaurantIds,
  properties: {
    rewardData: {
      ...values.reward_data.properties,
      articleId: values.reward_data.properties.articleId ? Number(values.reward_data.properties.articleId) : null,
      priceGroupId: Number(values.reward_data.properties.priceGroupId)
    }
  },
  ...(values.reward_data.images?.mainImage && {
    images: {
      mainImage: values.reward_data.images.mainImage
    }
  })
}); 